import React, { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import Alert from "@cloudscape-design/components/alert";
import Button from "@cloudscape-design/components/button";
import FormField from "@cloudscape-design/components/form-field";
import Input from "@cloudscape-design/components/input";
import Modal from "@cloudscape-design/components/modal";
import Select from "@cloudscape-design/components/select";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Textarea from "@cloudscape-design/components/textarea";
import Toggle from "@cloudscape-design/components/toggle";

import { notificationTagsStore } from "../stores/notificationTags";

const NAME_TYPE_OPTIONS = ["local", "international", "mixed"].map((value) => ({ value, label: value }));
const EMPTY_FORM = { key: "", name: "", nameType: "local", sortOrder: 0, active: true };
const SYSTEM_TAG_KEYS = new Set(["all", "unread"]);

const getErrorText = (error, fallback) => error?.text || error?.message || (typeof error === "string" ? error : fallback);

const NotificationTagsModal = ({ Interface }) => {
	const [store, setStore] = useRecoilState(notificationTagsStore);
	const [form, setForm] = useState(EMPTY_FORM);
	const [error, setError] = useState("");
	const [saving, setSaving] = useState(false);
	const isEdit = store.mode === "edit" && Boolean(store.editingTag?.id);
	const isSystem = isEdit && (Boolean(store.editingTag?.system) || SYSTEM_TAG_KEYS.has(store.editingTag?.key));

	useEffect(() => {
		if (!store.show) return;

		const tag = isEdit ? store.editingTag : null;
		setForm(
			tag
				? {
						key: tag.key || "",
						name: tag.name || "",
						nameType: tag.nameType || "local",
						sortOrder: Number(tag.sortOrder || 0),
						active: Number(tag.active) === 1,
					}
				: EMPTY_FORM,
		);
		setError("");
	}, [isEdit, store.editingTag, store.show]);

	useEffect(() => {
		if (!store.show || store.loaded) return;
		Interface.loadNotificationTags().catch(() => {});
	}, [Interface, store.loaded, store.show]);

	const close = () => {
		if (saving) return;
		setError("");
		setStore((prev) => ({ ...prev, show: false, mode: "create", editingTag: null }));
	};

	const saveTag = async () => {
		const key = form.key.trim();
		const name = form.name.trim();
		const sortOrder = Number(form.sortOrder);
		if (!isSystem && !/^[a-z]{1,64}$/.test(key)) {
			setError("Key must contain only a-z and be between 1 and 64 characters.");
			return;
		}
		if (!isSystem && SYSTEM_TAG_KEYS.has(key)) {
			setError(`Key '${key}' is reserved for a system tag.`);
			return;
		}
		if (!isSystem && !name) {
			setError("Name is required.");
			return;
		}
		if (!Number.isInteger(sortOrder) || sortOrder < 0) {
			setError("Order must be a non-negative integer.");
			return;
		}

		try {
			setSaving(true);
			setError("");
			const action = isEdit ? "updateTag" : "createTag";
			const result = await Interface.request(
				"set",
				{ class: "data", module: "notification", method: "edit" },
				{
					action,
					id: isEdit ? Number(store.editingTag.id) : undefined,
					tag: isSystem ? { sortOrder } : { key, name, nameType: form.nameType, sortOrder, active: form.active ? 1 : 0 },
				},
			);
			setStore((prev) => ({ ...prev, data: result.tags || [], error: "", loaded: true, show: false, mode: "create", editingTag: null }));
			Interface.addNoty("Success", isEdit ? "Notification tag updated" : "Notification tag created");
		} catch (requestError) {
			setError(getErrorText(requestError, isEdit ? "Failed to update notification tag" : "Failed to create notification tag"));
		} finally {
			setSaving(false);
		}
	};

	return (
		<Modal
			visible={store.show}
			onDismiss={close}
			header={isEdit ? "Edit notification tag" : "Create notification tag"}
			footer={
				<SpaceBetween direction="horizontal" size="xs">
					<Button onClick={close} disabled={saving}>
						Cancel
					</Button>
					<Button variant="primary" onClick={saveTag} loading={saving} disabled={!store.loaded}>
						{isEdit ? "Save changes" : "Create tag"}
					</Button>
				</SpaceBetween>
			}>
			<SpaceBetween size="m">
				{(error || store.error) && <Alert type="error">{error || store.error}</Alert>}
				{isSystem && <Alert type="info">This is a system tag. Only its display order can be changed.</Alert>}
				{!isSystem && (
					<>
						<FormField label="Key" stretch description="Unique key: lowercase a-z only, maximum 64 characters">
							<Input value={form.key} onChange={({ detail }) => setForm((prev) => ({ ...prev, key: detail.value }))} disabled={saving} />
						</FormField>
						<FormField label="Name type" stretch>
							<Select selectedOption={NAME_TYPE_OPTIONS.find((option) => option.value === form.nameType)} onChange={({ detail }) => setForm((prev) => ({ ...prev, nameType: detail.selectedOption.value }))} options={NAME_TYPE_OPTIONS} disabled={saving} />
						</FormField>
						<FormField label="Name" stretch description="Use the same local, international or mixed format as notification text">
							<Textarea value={form.name} onChange={({ detail }) => setForm((prev) => ({ ...prev, name: detail.value }))} disabled={saving} />
						</FormField>
					</>
				)}
				<FormField label="Order" stretch description="Lower values are displayed first">
					<Input type="number" value={String(form.sortOrder)} onChange={({ detail }) => setForm((prev) => ({ ...prev, sortOrder: detail.value }))} disabled={saving} />
				</FormField>
				{!isSystem && (
					<FormField label="Active" stretch description="Only active tags are displayed as tabs on the website">
						<Toggle checked={form.active} onChange={({ detail }) => setForm((prev) => ({ ...prev, active: detail.checked }))} disabled={saving}>
							{form.active ? "Active" : "Inactive"}
						</Toggle>
					</FormField>
				)}
			</SpaceBetween>
		</Modal>
	);
};

export default NotificationTagsModal;
